import React, { useMemo, memo } from 'react';
import { format } from 'date-fns';
import { ExportResult } from '../../../models';
import { DataTable, ReportLabels } from '../../../components/data-table';
import { TableColumn } from '@leasequery/lq.react-component-library/dist/models';
import { DATE_TIME_FORMAT } from '../../../constants';
import {
  TableCell,
  TableConfig,
} from '@leasequery/lq.react-component-library/dist/components/table';

import ExportNameCell from './ExportNameCell';
import { escapeRegExp, noop } from '../../../util';
import { useAuth } from '../../../context';
import createStatusCell from './createStatusCell';
import createExportToExcelButtonCell from './createExportToExcelButtonCell';

interface RecentExportsTableProps {
  exportResults: ExportResult[];
}

const tableConfig: TableConfig = {
  globalFilterDetector: (item: ExportResult, value: string) =>
    new RegExp(escapeRegExp(value), 'i').test(item.taskName),
};

const createTableColumns = (isMultiClientUser?: boolean): TableColumn[] => {
  const StatusCell = createStatusCell();
  const ExportToExcelButtonCell = createExportToExcelButtonCell();
  const columns: TableColumn[] = [
    {
      Header: 'Process Name',
      Cell: ExportNameCell,
      accessor: 'taskName',
    },
    {
      Header: 'Status',
      Cell: StatusCell,
      accessor: 'statusId',
      width: '8rem',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      Header: 'Run Date',
      Cell: (cell: TableCell): string => {
        return cell.value
          ? format(cell.value, DATE_TIME_FORMAT.FULL_DATE_TIME_WITH_MONTH_NAME)
          : null;
      },
      accessor: 'requestedTime',
      width: '19rem',
    },
    {
      Header: 'Result',
      Cell: ExportToExcelButtonCell,
      accessor: 'ExportToExcel',
      width: '16rem',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
  ];

  if (isMultiClientUser) {
    columns.push({
      Header: 'Client',
      accessor: 'companyName',
    });
  }

  return columns;
};

const labels: ReportLabels = {
  itemCount: 'Export Count:',
  searchPlaceHolder: 'Search Export Results',
};

function RecentExportsTable({
  exportResults,
}: RecentExportsTableProps): JSX.Element {
  const { user } = useAuth();
  const columns = useMemo(() => createTableColumns(user.isMultiClientUser), [user]);
  return (
    <DataTable
      columns={columns}
      data={exportResults}
      config={tableConfig}
      labels={labels}
    />
  );
}

RecentExportsTable.defaultProps = {
  onArchiveClick: noop,
};

export default memo(RecentExportsTable);
